import {Nullable} from '../../app/TypeHelpers'
import {
    ACCESS_CONTROL_MANAGER,
    AVAILABLE_OR_NOT,
    CHARGE_UNIT,
    FACILITY_CATEGORY,
    PAYMENT_TYPE,
    PUBLISHING_SETTING,
    RELEASE_START_HOURS,
    RESERVATION_ACCEPTABILITY_FOR_RESIDENT,
    UNIT_OF_BUSINESS_SCHEDULE,
    UNIT_TIME,
    VALID_OR_NOT,
} from '../../CodeMaster'
import {Duration, Moment} from 'moment'
import {fromServerTimeToDuration, fromServerTimeToMoment} from '../../dateHelpers/MomentHelper'

export interface ImageLocation {
    fileNumber: number
    url: string
}

export interface ReservationSetting {
    residentReserve: RESERVATION_ACCEPTABILITY_FOR_RESIDENT
    lotteryAvailability: AVAILABLE_OR_NOT
    lotteryPriority: Nullable<number>
    reserveReceptionStartMonth: Nullable<number>
    reserveReceptionStartDate: Nullable<number>
    reserveReceptionLimitDate: Nullable<number>
    reserveReceptionLimitTime: Nullable<Duration>
    cancelAvailability: AVAILABLE_OR_NOT
    cancelReceptionLimitDate: Nullable<number>
    cancelReceptionLimitTime: Nullable<Duration>
}

function getReservationSetting(json: any): ReservationSetting {
    const {
        residentReserve,
        lotteryAvailability,
        reserveReceptionLimitTime,
        cancelAvailability,
        cancelReceptionLimitTime,
        ...rest
    } = json
    return {
        residentReserve: RESERVATION_ACCEPTABILITY_FOR_RESIDENT.codeToEnumConstants(residentReserve),
        lotteryAvailability: AVAILABLE_OR_NOT.codeToEnumConstants(lotteryAvailability),
        reserveReceptionLimitTime: reserveReceptionLimitTime === null ? null :
            fromServerTimeToDuration(reserveReceptionLimitTime),
        cancelAvailability: AVAILABLE_OR_NOT.codeToEnumConstants(cancelAvailability),
        cancelReceptionLimitTime: cancelReceptionLimitTime === null ? null :
            fromServerTimeToDuration(cancelReceptionLimitTime),
        ...rest,
    }
}

export interface FacilityOption {
    name: string
    optionId: number
    sortOrder: number
    charge: number
    chargeUnitTime: Nullable<number>
    chargeUnit: CHARGE_UNIT
    volumeMin: number
    volumeMax: number
    unitName: string
    paymentMethod: PAYMENT_TYPE
    appointRequired: VALID_OR_NOT
}

function getFacilityOption(json: any): FacilityOption {
    const {chargeUnit, paymentMethod, appointRequired, ...rest} = json
    return {
        chargeUnit: CHARGE_UNIT.codeToEnumConstants(chargeUnit),
        paymentMethod: PAYMENT_TYPE.codeToEnumConstants(paymentMethod),
        appointRequired: VALID_OR_NOT.codeToEnumConstants(appointRequired),
        ...rest,
    }
}

export interface StaySetting {
    stayPossibleDateCount: number
    stayContinueTimeRental: AVAILABLE_OR_NOT
    checkinTime: Moment
    checkoutTime: Moment
    stayCharge: number
}

function getStaySetting(json: any): StaySetting {
    const {stayContinueTimeRental, checkinTime, checkoutTime, ...rest} = json
    return {
        stayContinueTimeRental: AVAILABLE_OR_NOT.codeToEnumConstants(stayContinueTimeRental),
        checkinTime: fromServerTimeToMoment(checkinTime),
        checkoutTime: fromServerTimeToMoment(checkoutTime),
        ...rest,
    }
}

export interface ReservationTimeSection {
    startTime: Moment
    maxReservationTime: number
}

function getReservationTimeSection(json: any): ReservationTimeSection {
    const {startTime, maxReservationTime} = json
    return {
        startTime: fromServerTimeToMoment(startTime),
        maxReservationTime: maxReservationTime,
    }
}

export interface MaxReservationTimeSetting {
    allDayMaxReservationTime: Nullable<number>
    reservationTimeSections: Nullable<ReservationTimeSection[]>
}

function getMaxReservationTimeSetting(json: any): MaxReservationTimeSetting {
    const {allDayMaxReservationTime, reservationTimeSections} = json
    return {
        allDayMaxReservationTime: allDayMaxReservationTime,
        reservationTimeSections: reservationTimeSections === null ? null :
            reservationTimeSections.map((section: any) => getReservationTimeSection(section)),
    }
}

export interface BusinessSchedule {
    businessScheduleUnit: UNIT_OF_BUSINESS_SCHEDULE
    dateStraddle: Nullable<VALID_OR_NOT>
    joinPrevDaysSchedule: Nullable<VALID_OR_NOT>
    startTime: Nullable<Moment>
    endTime: Nullable<Moment>
    charge: Nullable<number>
    chargeUnit: Nullable<CHARGE_UNIT>
    chargeUnitTime: Nullable<number>
    changedChargeApplyStartTime: Nullable<number>
    changedChargeApplyStartTimeUnit: Nullable<UNIT_TIME>
    changedCharge: Nullable<number>
    changedChargeUnit: Nullable<CHARGE_UNIT>
    changedChargeUnitTime: Nullable<number>
}

function getBusinessSchedule(json: any): BusinessSchedule {
    const {
        businessScheduleUnit,
        dateStraddle,
        joinPrevDaysSchedule,
        startTime,
        endTime,
        chargeUnit,
        changedChargeApplyStartTimeUnit,
        changedChargeUnit,
        ...rest
    } = json
    return {
        businessScheduleUnit: UNIT_OF_BUSINESS_SCHEDULE.codeToEnumConstants(businessScheduleUnit),
        dateStraddle: dateStraddle === null ? null : VALID_OR_NOT.codeToEnumConstants(dateStraddle),
        joinPrevDaysSchedule: joinPrevDaysSchedule === null ?
            null : VALID_OR_NOT.codeToEnumConstants(joinPrevDaysSchedule),
        startTime: startTime === null ? null : fromServerTimeToMoment(startTime),
        endTime: endTime === null ? null : fromServerTimeToMoment(endTime),
        chargeUnit: chargeUnit === null ? null : CHARGE_UNIT.codeToEnumConstants(chargeUnit),
        changedChargeApplyStartTimeUnit: changedChargeApplyStartTimeUnit === null ?
            null : UNIT_TIME.codeToEnumConstants(changedChargeApplyStartTimeUnit),
        changedChargeUnit: changedChargeUnit === null ? null : CHARGE_UNIT.codeToEnumConstants(changedChargeUnit),
        ...rest,
    }
}

export interface Equipment {
    id: number,
    name: string
}

export default class FacilityDetails {
    sortOrder: number
    category: FACILITY_CATEGORY
    equipments: Equipment[]
    facilityName: string
    accessControlManager: Nullable<ACCESS_CONTROL_MANAGER>
    cardKeyFacilityNumber: Nullable<string>
    gateNumbers: string[]
    images: ImageLocation[]
    reservationSetting: ReservationSetting
    facilityIntroductory: Nullable<string>
    optionSettings: FacilityOption[]
    releaseSetting: PUBLISHING_SETTING
    releaseStartDate: Nullable<string>
    releaseStartTime: Nullable<RELEASE_START_HOURS>
    reserveBeforeCleaningTime: number
    reserveAfterCleaningTime: number
    staySetting: Nullable<StaySetting>
    maxReservationTimeSetting: Nullable<MaxReservationTimeSetting>
    businessSchedules: Nullable<BusinessSchedule[]>
    version: number

    constructor(
        sortOrder: number,
        category: FACILITY_CATEGORY,
        equipments: Equipment[],
        facilityName: string,
        accessControlManger: Nullable<ACCESS_CONTROL_MANAGER>,
        cardKeyFacilityNumber: string | null,
        gateNumbers: string[],
        images: ImageLocation[],
        reservationSetting: ReservationSetting,
        facilityIntroductory: string | null,
        optionSettings: FacilityOption[],
        releaseSetting: PUBLISHING_SETTING,
        releaseStartDate: Nullable<string>,
        releaseStartTime: Nullable<RELEASE_START_HOURS>,
        reserveBeforeCleaningTime: number,
        reserveAfterCleaningTime: number,
        staySetting: StaySetting | null,
        maxReservationTimeSetting: MaxReservationTimeSetting | null,
        businessSchedules: BusinessSchedule[] | null,
        version: number,
    ) {
        this.sortOrder = sortOrder
        this.category = category
        this.equipments = equipments
        this.facilityName = facilityName
        this.accessControlManager = accessControlManger
        this.cardKeyFacilityNumber = cardKeyFacilityNumber
        this.gateNumbers = gateNumbers
        this.images = images
        this.reservationSetting = reservationSetting
        this.facilityIntroductory = facilityIntroductory
        this.optionSettings = optionSettings
        this.releaseSetting = releaseSetting
        this.releaseStartDate = releaseStartDate
        this.releaseStartTime = releaseStartTime
        this.reserveBeforeCleaningTime = reserveBeforeCleaningTime
        this.reserveAfterCleaningTime = reserveAfterCleaningTime
        this.staySetting = staySetting
        this.maxReservationTimeSetting = maxReservationTimeSetting
        this.businessSchedules = businessSchedules
        this.version = version
    }

    static from(json: any): FacilityDetails {
        return new FacilityDetails(
            json.sortOrder,
            FACILITY_CATEGORY.codeToEnumConstants(json.categoryId)!,
            json.equipments.map((equipment: any) => ({id: equipment.id, name: equipment.name})),
            json.facilityName,
            json.accessControlManager === null ? null :
                ACCESS_CONTROL_MANAGER.codeToEnumConstants(json.accessControlManager),
            json.cardKeyFacilityNumber,
            json.gateNumbers,
            json.images,
            getReservationSetting(json.reservationSetting),
            json.facilityIntroductory,
            json.optionSettings === null ? [] :
                json.optionSettings.map((optionJson: any) => getFacilityOption(optionJson)),
            PUBLISHING_SETTING.codeToEnumConstants(json.releaseSetting)!,
            json.releaseStartDate,
            json.releaseStartTime === null ? null :
                RELEASE_START_HOURS.codeToEnumConstants(json.releaseStartTime)!,
            json.reserveBeforeCleaningTime,
            json.reserveAfterCleaningTime,
            json.staySetting === null ? null : getStaySetting(json.staySetting),
            json.maxReservationTimeSetting === null ? null :
                getMaxReservationTimeSetting(json.maxReservationTimeSetting),
            json.businessSchedules === null ? null :
                json.businessSchedules.map((schedule: any) => getBusinessSchedule(schedule)),
            json.version,
        )
    }
}
